import React, { useState, useRef, useEffect } from "react";
import { MessageCircle, Radio } from "lucide-react";

/**
 * NoorVideoButton — floating action button showing a looping muted video of Noor.
 * Clicking triggers the main bot to open and plays the video with audio.
 */
export default function NoorVideoButton({ lang, onOpenBot, onOpenLive }) {
    const [expanded, setExpanded] = useState(false);
    const [showPulse, setShowPulse] = useState(true);
    const videoRef = useRef(null);
    const isAr = lang === "ar";

    // Auto-play muted loop when mounted; browsers require muted for autoplay
    useEffect(() => {
        if (videoRef.current) {
            videoRef.current.play().catch(() => {});
        }
        // Hide the pulse ring after 6 s to reduce visual noise once user has noticed it
        const t = setTimeout(() => setShowPulse(false), 6000);
        return () => clearTimeout(t);
    }, []);

    const handleClickBubble = () => {
        // Toggle expanded card with the two options
        setExpanded((v) => !v);
        setShowPulse(false);
    };

    const openLive = () => {
        setExpanded(false);
        if (onOpenLive) onOpenLive();
    };

    const openChat = () => {
        setExpanded(false);
        if (onOpenBot) onOpenBot();
    };

    return (
        <div
            className="fixed z-[80] bottom-5 end-5 md:bottom-6 md:end-6"
            data-testid="noor-video-button"
        >
            {/* Expanded action card — two choices */}
            {expanded && (
                <div className="absolute bottom-full end-0 mb-3 w-72 bg-white rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.35)] border border-[#F1E4D2] p-3 animate-in fade-in slide-in-from-bottom-2 duration-300">
                    <div className="text-xs font-bold text-[#B36318] mb-2">
                        {isAr ? "كيف تريد التحدث مع نور؟" : "איך תרצה לדבר עם נור?"}
                    </div>
                    <button
                        onClick={openLive}
                        data-testid="noor-btn-open-live"
                        className="w-full mb-2 flex items-center gap-3 px-3 py-2.5 rounded-xl bg-gradient-to-r from-[#D4761F] to-[#D4AF37] text-white hover:brightness-110 transition group"
                    >
                        <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center shrink-0">
                            <Radio size={16} />
                        </div>
                        <div className="text-start flex-1">
                            <div className="text-[13px] font-bold flex items-center gap-1.5">
                                {isAr ? "محادثة صوتية مباشرة" : "שיחה קולית חיה"}
                                <span className="text-[8px] bg-white text-[#B36318] px-1 py-0.5 rounded font-black">BETA</span>
                            </div>
                            <div className="text-[10px] opacity-80">
                                {isAr ? "تحدث بصوتك · نور ترد بالصوت" : "דבר בקולך · נור עונה בקול"}
                            </div>
                        </div>
                    </button>
                    <button
                        onClick={openChat}
                        data-testid="noor-btn-open-chat"
                        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl bg-[#F8F5EE] hover:bg-[#F1E4D2] text-[#1A365D] transition"
                    >
                        <div className="w-9 h-9 rounded-full bg-[#1A365D] text-white flex items-center justify-center shrink-0">
                            <MessageCircle size={16} />
                        </div>
                        <div className="text-start flex-1">
                            <div className="text-[13px] font-bold">
                                {isAr ? "دردشة نصية" : "צ'אט טקסט"}
                            </div>
                            <div className="text-[10px] text-[#475569]">
                                {isAr ? "اكتب واحصل على إجابات فورية" : "כתוב וקבל תשובות מיידיות"}
                            </div>
                        </div>
                    </button>
                </div>
            )}

            <button
                onClick={handleClickBubble}
                data-testid="noor-video-btn-open"
                aria-label={isAr ? "افتح نور" : "פתח את נור"}
                className="relative group"
            >
                {/* Pulse rings (only for first few seconds to grab attention) */}
                {showPulse && (
                    <>
                        <span className="absolute inset-0 rounded-full border-2 border-[#D4761F]/60 animate-ping" style={{ animationDuration: "2s" }} />
                        <span className="absolute inset-0 rounded-full border-2 border-[#D4AF37]/40 animate-ping" style={{ animationDuration: "3s", animationDelay: "0.5s" }} />
                    </>
                )}

                {/* Video circle */}
                <div className="relative rounded-full overflow-hidden border-[3px] border-[#D4AF37] shadow-[0_15px_35px_rgba(212,118,31,0.5)] group-hover:shadow-[0_20px_50px_rgba(212,118,31,0.65)] transition-all duration-300 w-20 h-20 md:w-24 md:h-24 group-hover:scale-105 bg-[#0B1F3D]">
                    <video
                        ref={videoRef}
                        src="/noor-demo-v3.mp4"
                        muted
                        loop
                        playsInline
                        preload="auto"
                        className="w-full h-full object-cover"
                    />
                    {/* Live badge */}
                    <div className="absolute bottom-1 start-1/2 -translate-x-1/2 rtl:translate-x-1/2 bg-gradient-to-r from-[#DC2626] to-[#991B1B] text-white text-[8px] font-bold px-2 py-0.5 rounded-full flex items-center gap-1 shadow-md whitespace-nowrap">
                        <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
                        LIVE
                    </div>
                </div>

                {/* Floating chat icon (small badge over video) */}
                <div className="absolute -top-1 -end-1 w-7 h-7 rounded-full bg-gradient-to-br from-[#D4761F] to-[#E08A30] flex items-center justify-center shadow-lg border-2 border-white">
                    <MessageCircle size={13} className="text-white" />
                </div>

                {/* Hover label */}
                <div className="absolute end-full me-3 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap bg-[#0B1F3D] text-white text-xs font-bold px-3 py-2 rounded-lg shadow-lg">
                    {isAr ? "💬 اسأل نور" : "💬 שאל את נור"}
                </div>
            </button>
        </div>
    );
}

